import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';
import VentureResult from '../Venture/VentureResult';
import PlanContainer from '../Plan/PlanContainer';
import { useChat } from '@/hooks/useChat';
import { usePersona } from '@/hooks/usePersona';

const ChatContainer = () => {
  const { messages, isSending, sendMessage, planItOut, advancePlan, getPlanState, ventureContext, activeConversationId } = useChat();
  const { currentPersona } = usePersona();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  const activePlanState = getPlanState();

  return (
    <div className="flex flex-col h-full">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto no-scrollbar space-y-3 py-4 overflow-x-hidden"
        style={{ minWidth: 0 }}
      >
        <AnimatePresence mode="popLayout">
          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center justify-center h-full text-center px-6"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                className="w-20 h-20 rounded-3xl gradient-bg flex items-center justify-center mb-6 shadow-glow"
              >
                <span className="text-4xl">🤖</span>
              </motion.div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2 tracking-tight">
                Welcome to Sheehan AI
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs leading-relaxed">
                Ask me anything, brainstorm ideas, or dive deep into research. I'm here to help!
              </p>
              <div className="mt-8 flex flex-wrap gap-2 justify-center">
                {['Explain quantum computing', 'Plan a family vacation', 'Write Python code', 'Latest news summary'].map((suggestion) => (
                  <motion.button
                    key={suggestion}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => sendMessage(suggestion)}
                    className="px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                               text-xs text-slate-600 dark:text-slate-300 hover:border-primary-300 dark:hover:border-primary-700
                               transition-colors shadow-sm"
                  >
                    {suggestion}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {messages.map((message, index) => (
            <MessageBubble key={message.id} message={message} index={index} />
          ))}

          {isSending && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <TypingIndicator />
            </motion.div>
          )}

          {currentPersona === 'venture' && ventureContext?.action && activeConversationId && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <VentureResult
                context={ventureContext}
                onPlanItOut={() => planItOut(activeConversationId)}
              />
            </motion.div>
          )}

          {currentPersona === 'plan' && activePlanState && activeConversationId && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <PlanContainer
                planState={activePlanState}
                onAdvance={() => advancePlan(activeConversationId)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ChatInput onSend={sendMessage} disabled={isSending} />
    </div>
  );
};

export default ChatContainer;
